// src/AboutUs.js
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import FeaturesShowcase from "./About1";
import WhyChoose from "./WhyChoose";

// Mobile detection
function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

const PALETTE = {
  ivory: "#F7F1E8",
  charcoal: "#1B1B1B",
  tan: "#C3A585",
  tanHover: "#b59473",
  textDark: "#2A2A2A",
  textLight: "#FFFFFF",
  subDark: "#6C6C6C",
  subLight: "#D2D2D2",
  lineLight: "#DCD2C5",
  lineDark: "rgba(255,255,255,0.18)",
};

// Sequential word appear animation
function WordSequence() {
  const words = ["DESIGN", "COORDINATE", "DELIVER", "THAT’S MANAR"];

  const container = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 1, // delays each child appearance
      },
    },
  };

  const child = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  return (
    <motion.div
      variants={container}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="mt-10 text-center space-y-2"
    >
      {words.map((word, i) => (
        <motion.div
          key={i}
          variants={child}
          className="text-[30px] md:text-[38px] font-semibold tracking-[0.12em] text-neutral-700"
        >
          {word}
        </motion.div>
      ))}
    </motion.div>
  );
}


export default function AboutUs({ includeHeaderOffset = true } = {}) {
  const prefersReducedMotion = useReducedMotion();
  const isMobile = useIsMobile();
  const containerClassName = `w-full antialiased ${includeHeaderOffset ? "pt-[65px]" : ""}`;

  const getMotionProps = (opts = {}) => {
    if (prefersReducedMotion) return {};
    if (isMobile) {
      return {
        initial: { opacity: 0, y: opts.y || 100 },
        whileInView: { opacity: 1, y: 0 },
        viewport: { once: true, amount: 0.3 },
        transition: {
          duration: opts.duration || 1.2,
          delay: opts.delay || 0,
          ease: opts.ease || "easeOut",
        },
      };
    } else {
      return {
        initial: opts.initial,
        animate: opts.animate,
        transition: {
          duration: opts.duration || 1.2,
          delay: opts.delay || 0,
          ease: opts.ease || "easeOut",
        },
      };
    }
  };

  return (
    <div
      className={containerClassName}
      style={{ backgroundColor: PALETTE.ivory, color: PALETTE.textDark }}
    >
      {/* Hero */}
      <section
        className="relative w-full overflow-clip"
        style={{ backgroundColor: PALETTE.ivory, minHeight: "550px" }}
      >
        {/* Background visuals */}
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute -top-40 left-1/2 h-[60vh] w-[80vw] -translate-x-1/2 rounded-[999px] blur-3xl"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(195,165,133,0.32), transparent 60%)",
            }}
          />
          <div
            className="absolute bottom-0 left-1/3 h-[50vh] w-[60vw] -translate-x-1/2 rounded-[999px] blur-3xl"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(210,210,210,0.28), transparent 60%)",
            }}
          />
        </div>

        {/* Light texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='2' stitchTiles='stitch'/></filter><rect width='200' height='200' filter='url(%23n)' fill='rgba(0,0,0,0.04)'/></svg>\")",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/40 via-white/15 to-white/50" />

        {/* Grid layout */}
        <div className="relative z-10 grid grid-cols-1 md:grid-cols-2" style={{ minHeight: "550px" }}>
          {/* Left content */}
          <div
            className="relative flex items-center justify-center px-6 py-8 md:py-0 overflow-hidden min-w-0"
            onMouseMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              const x = ((e.clientX - r.left) / r.width) * 100;
              const y = ((e.clientY - r.top) / r.height) * 100;
              e.currentTarget.style.setProperty("--x", `${x}%`);
              e.currentTarget.style.setProperty("--y", `${y}%`);
            }}
          >
            <div className="pointer-events-none absolute inset-0 [mask-image:radial-gradient(220px_220px_at_var(--x,50%)_var(--y,50%),white,transparent)]">
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(circle at center, rgba(0,0,0,0.06), transparent 60%)",
                }}
              />
            </div>

            {/* Hero content */}
            <div className="relative z-10 w-full max-w-[650px] md:max-w-[36rem]">
              {/* Rotating logo */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <motion.img
                  src="./images/logo2.png"
                  alt="Manar Logo"
                  className="w-[clamp(180px,60vw,260px)] md:w-[clamp(220px,35vw,350px)] translate-y-6"
                  animate={prefersReducedMotion ? undefined : { rotateY: [0, 360] }}
                  transition={
                    prefersReducedMotion
                      ? undefined
                      : { repeat: Infinity, duration: 25, ease: "linear" }
                  }
                  style={{ backfaceVisibility: "visible", opacity: 0.5 }}
                />
              </div>

              {/* Title */}
              <motion.h1
                className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-tight bg-clip-text text-transparent text-center"
                style={{
                  backgroundImage: `linear-gradient(to right, ${PALETTE.tan}, ${PALETTE.textDark}, ${PALETTE.subDark})`,
                }}
                {...getMotionProps({
                  initial: { opacity: 0, y: -120 },
                  animate: { opacity: 1, y: 0 },
                  duration: 1.2,
                  delay: 0.7,
                  y: -60,
                })}
              >
                Storyteller of Spaces
              </motion.h1>

              {/* Subtitle */}
              <motion.p
                className="mt-5 text-base md:text-lg leading-relaxed"
                style={{ color: PALETTE.subDark }}
                {...getMotionProps({
                  initial: { opacity: 0, y: 160 },
                  animate: { opacity: 1, y: 0 },
                  duration: 0.9,
                  delay: 0.35,
                  y: 100,
                })}
              >
                “MANAR IS A DESIGN AND COORDINATION MANAGEMENT FIRM COMMITTED TO
                PROVIDING COMPREHENSIVE SOLUTIONS IN ARCHITECTURAL PLANNING AND
                PROJECT EXECUTION, ENSURING CLARITY, ALIGNMENT, AND EXCELLENCE
                AT EVERY STAGE.”
              </motion.p>

              {/* Sequential animated words */}
              <WordSequence />
            </div>
          </div>

          {/* Right video */}
          <div className="relative" style={{ height: "550px" }}>
            <video
              id="hero-video"
              className="h-full w-full object-cover"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
            >
              <source src="./images/i/video.mp4" type="video/mp4" />
            </video>
          </div>
        </div>
      </section>

      {/* Additional sections */}
      <WhyChoose />
      <FeaturesShowcase />
    </div>
  );
}
